import { toast } from "../../toast/toast";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Reagent, ReagentLog, Unit } from "../../../../backend/src/types";
import { useUser } from "../../contexts/UserContext";
import { getReagentLogList } from "../../fetch/logRequest";
import { errorMessage } from "../../function/errorMessage";
import { toJSTDateString } from "../../function/toJSTDateString";
import styles from "../../styles/modules/LogTab.module.css";
import "../../styles/sanitize.css";

interface LogTabProps {
  reagent: Reagent;
  units: Unit[];
  onLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

type DispReagentLog = Pick<
  ReagentLog,
  | "id"
  | "action"
  | "quantity_in"
  | "quantity_used"
  | "quantity_remaining"
  | "user_id"
  | "remarks"
> & {
  user_name: string | null;
  user_num: string | null;
  manager_name: string | null;
  created_at: string;
  display_unit: Unit;
};

const LogTab: React.FC<LogTabProps> = ({ onLoading, units, reagent }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<DispReagentLog[]>([]);

  useEffect(() => {
    if (user) loadDispLog(reagent);
  }, [user, reagent]);

  //DispLogを返すようにする。
  async function loadDispLog(reagent: Reagent) {
    onLoading(true);
    const result = await getReagentLogList({
      reagent_id: reagent.id,
    });

    if (!result) {
      return;
    }

    const logList = result.logs;
    const users = result.users;
    const managers = result.managers;

    const DispReagentLogTypeList: DispReagentLog[] = [];

    for (const log of logList) {
      if (log.created_at == null) {
        continue;
      }

      let user_name: string | null = null;
      let user_num: string | null = null;
      let manager_name: string | null = null;
      let created_at: string = "";
      const userId = log.user_id;
      const managerId = log.manager_id;

      if (userId) {
        const display_user = users.find((user) => user.id === userId);
        if (display_user) {
          user_name = display_user.name;
          user_num = display_user.student_no;
        }
      }
      // 利用者が完全削除されている場合（user_id が NULL）は、
      // ログに控えた名前スナップショットで表示する。
      if (!user_name && log.user_name) {
        user_name = log.user_name;
      }

      if (managerId) {
        const display_manager = managers.find(
          (managers) => managers.id === managerId
        );
        if (display_manager) {
          manager_name = display_manager.name;
        }
      }
      if (!manager_name && log.manager_name) {
        manager_name = log.manager_name;
      }

      const date = new Date(log.created_at);

      if (isNaN(date.getTime())) {
        toast.error("日付の取得に失敗しました。");
        return;
      } else {
        created_at = toJSTDateString(log.created_at);
      }

      const display_unit = units.find(
        (unit) => unit.code === reagent.display_unit
      );

      if (display_unit == null) {
        continue;
      }

      const to_base_factor = display_unit.to_base_factor;

      const quantity_used =
        log.quantity_used !== null ? log.quantity_used / to_base_factor : null;

      const quantity_in =
        log.quantity_in !== null ? log.quantity_in / to_base_factor : null;

      const quantity_remaining =
        log.quantity_remaining !== null
          ? log.quantity_remaining / to_base_factor
          : null;

      const dispLog: DispReagentLog = {
        ...log,
        user_name,
        user_num,
        manager_name,
        created_at,
        display_unit,
        quantity_used,
        quantity_in,
        quantity_remaining,
      };

      DispReagentLogTypeList.push(dispLog);
    }

    // 生成された id の順にソート
    const sortedLogs = DispReagentLogTypeList.sort((a, b) => {
      return new Date(a.id).getTime() - new Date(b.id).getTime();
    });

    setLogs(sortedLogs);
    onLoading(false);
  }

  async function download() {
    try {
      const csvHeader = [
        "No",
        "年月日",
        "受入量",
        "使用量",
        "在庫量",
        "学籍番号",
        "使用者",
        "責任者",
        "備考",
      ];

      const csvRows = logs.map((log, index) => [
        index + 1,
        log.created_at,
        log.quantity_in != null
          ? `${log.quantity_in}${log.display_unit?.code ?? ""}`
          : "",
        log.quantity_used != null
          ? `${log.quantity_used}${log.display_unit?.code ?? ""}`
          : "",
        log.quantity_remaining != null
          ? `${log.quantity_remaining}${log.display_unit?.code ?? ""}`
          : "",
        log.user_num ?? "",
        log.user_name ?? "",
        log.manager_name ?? "",
        log.remarks ?? "",
      ]);

      const csvContent =
        "\uFEFF" +
        [csvHeader, ...csvRows]
          .map((row) => row.map((val) => `${val}`).join(","))
          .join("\n");

      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");

      const year = now.getFullYear();
      const month = pad(now.getMonth() + 1);
      const day = pad(now.getDate());
      const hour = pad(now.getHours());
      const minute = pad(now.getMinutes());

      const nowDay = `${year}${month}${day}${hour}${minute}`;
      const reagentName = reagent.name
        .replace(/[\\\/:*?"<>|]/g, "")
        .trim()
        .slice(0, 150);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `logs_${reagentName}_${nowDay}.csv`;
      link.click();
      return;
    } catch (error) {
      errorMessage("CSVのダウンロードでエラーが発生しました。", error);
      return;
    }
  }

  return (
    <div className={styles["log-container"]}>
      <div className={styles["list-box"]}>
        <table>
          <thead>
            <tr>
              <th>日付</th>
              <th>受入量</th>
              <th>使用量</th>
              <th>在庫量</th>
              <th>学籍番号</th>
              <th>使用者</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td>{log.created_at}</td>
                <td>
                  {log.quantity_in !== null &&
                    `${log.quantity_in}${log.display_unit.code}`}
                </td>
                <td>
                  {log.quantity_used !== null &&
                    `${log.quantity_used}${log.display_unit.code}`}
                </td>
                <td>
                  {log.quantity_remaining !== null &&
                    `${log.quantity_remaining}${log.display_unit.code}`}
                </td>
                <td>{log.user_num}</td>
                <td>{log.user_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className={styles["csv-download"]}
        onClick={() => download()}
      >
        CSVダウンロード
      </button>
    </div>
  );
};

export default LogTab;
